import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionHeading";

const EXPERIENCE_IMG =
  "https://images.unsplash.com/photo-1709497197725-2e97c76b31d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const STAT_TRIO = [
  { n: 2018, suffix: "", label: "Established" },
  { n: 25, suffix: "+", label: "Happy clients" },
  { n: 5, suffix: "", label: "Countries served" },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1700;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

export const ExperienceParallax = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "14%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.25, 1.05]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-white noise-overlay"
      data-testid="experience-section"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0 pointer-events-none">
        <img src={EXPERIENCE_IMG} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-70" />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-white via-white/85 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-white via-transparent to-white" />

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-28">
        <SectionLabel>03 — Experience</SectionLabel>
        <h2 className="mt-8 max-w-4xl font-heading font-black tracking-tight text-4xl md:text-7xl leading-[1.02] text-brand-ink">
          Delivering with <span className="gradient-text">confidence</span> since 2018.
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-brand-ink/75">
            Since 2018, Tangensis has partnered with organizations to solve real business
            challenges — combining strategy, technology and talent to create measurable impact.
            We build our reputation one successful engagement at a time.
          </p>
        </Reveal>

        <div className="mt-16 grid max-w-2xl grid-cols-3 gap-6 md:gap-12">
          {STAT_TRIO.map((s, i) => (
            <Reveal key={s.label} delay={0.2 + i * 0.1}>
              <div>
                <div className="font-display font-black text-4xl md:text-7xl leading-none tracking-tighter gradient-text-animated">
                  <Counter value={s.n} suffix={s.suffix} />
                </div>
                <div className="mt-3 h-px w-10 bg-brand-ink/25" />
                <div className="mt-3 text-xs md:text-sm uppercase tracking-[0.15em] text-brand-ink/75">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.5}>
          <Link
            to="/services"
            data-testid="experience-cta"
            className="mt-14 inline-flex items-center gap-2 border-b-2 border-brand-orange pb-1 font-bold text-brand-ink transition-all duration-300 hover:gap-3"
          >
            EXPLORE OUR SERVICES <ArrowUpRight className="h-5 w-5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
